/**
 * Payment provider utilities — talks to /api/payments/providers to see
 * which providers are live, and offers a helper that opens the buyer's
 * chosen provider (Cashfree hosted checkout OR Razorpay Checkout.js).
 *
 * Consumers:
 *   import { openCheckoutWithChoice } from "../lib/paymentProvider";
 *   await openCheckoutWithChoice({
 *     appOrderId: "order_abc",
 *     amountInr: 4999,
 *     cashfreeSessionId: order.payment_session_id, // may be null
 *     buyer: { name, email, contact },
 *     onSuccess: (info) => navigate("/thanks"),
 *   });
 */
import api, { formatApiError } from "./api";
import { toast } from "sonner";

let _providersCache = null;

async function _loadProviders() {
  if (_providersCache) return _providersCache;
  try {
    const r = await api.get("/payments/providers");
    _providersCache = r.data;
    return _providersCache;
  } catch (e) {
    // Best-effort fallback so we never block checkout when the endpoint dies.
    console.warn("[payments] providers fetch failed:", e);
    return { providers: [], default: null };
  }
}

// -----------------------------------------------------------------------
// Razorpay Checkout.js loader — single-flight so we don't inject twice.
// -----------------------------------------------------------------------
let _rzpScriptPromise = null;
function _loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (_rzpScriptPromise) return _rzpScriptPromise;
  _rzpScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { _rzpScriptPromise = null; reject(new Error("Razorpay Checkout.js failed to load")); };
    document.body.appendChild(s);
  });
  return _rzpScriptPromise;
}

// -----------------------------------------------------------------------
// Razorpay flow: create server order → open Checkout.js → verify signature.
// -----------------------------------------------------------------------
async function _payWithRazorpay({ appOrderId, buyer, onSuccess, onFailure }) {
  await _loadRazorpayScript();
  let orderInfo;
  try {
    const r = await api.post("/payments/razorpay/order", { order_id: appOrderId });
    orderInfo = r.data;
  } catch (e) {
    toast.error(formatApiError(e));
    onFailure?.(e);
    return;
  }
  const rzp = new window.Razorpay({
    key: orderInfo.key_id,
    amount: orderInfo.amount,
    currency: orderInfo.currency || "INR",
    name: "VenturaZ",
    description: `Order ${appOrderId}`,
    order_id: orderInfo.razorpay_order_id,
    prefill: {
      name: buyer?.name || orderInfo.buyer?.name || "",
      email: buyer?.email || orderInfo.buyer?.email || "",
      contact: buyer?.contact || orderInfo.buyer?.contact || "",
    },
    notes: { app_order_id: appOrderId },
    theme: { color: "#1F5B4A" },
    modal: {
      ondismiss: () => {
        toast.info("Checkout dismissed — no charge made.");
        onFailure?.({ dismissed: true });
      },
    },
    handler: async (response) => {
      try {
        await api.post("/payments/razorpay/verify", {
          order_id: appOrderId,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        toast.success("Payment received — invoice will land in your inbox shortly.");
        onSuccess?.({ provider: "razorpay", payment_id: response.razorpay_payment_id });
      } catch (e) {
        toast.error(`Verification failed: ${formatApiError(e)}. Contact support.`);
        onFailure?.(e);
      }
    },
  });
  rzp.on("payment.failed", (resp) => {
    toast.error(`Razorpay: ${resp?.error?.description || "payment failed"}`);
    onFailure?.(resp);
  });
  rzp.open();
}

// -----------------------------------------------------------------------
// Cashfree flow — the existing SDK path (kept identical to before).
// -----------------------------------------------------------------------
async function _payWithCashfree({ cashfreeSessionId, onSuccess, onFailure }) {
  if (!cashfreeSessionId) {
    toast.error("Cashfree session not available for this order.");
    onFailure?.(new Error("no session id"));
    return;
  }
  try {
    // Lazy import so pages that never checkout don't pay the SDK cost.
    const mod = await import("@cashfreepayments/cashfree-js");
    const cashfree = await mod.load({ mode: "production" });
    cashfree.checkout({
      paymentSessionId: cashfreeSessionId,
      redirectTarget: "_self",
    });
    onSuccess?.({ provider: "cashfree" });
  } catch (e) {
    toast.error(`Cashfree load failed: ${e?.message || e}`);
    onFailure?.(e);
  }
}

// -----------------------------------------------------------------------
// Public: fetch providers + let caller present a picker OR go straight
// to the single available provider.
// -----------------------------------------------------------------------
export async function getPaymentProviders() {
  return _loadProviders();
}

/**
 * Start the checkout using a chosen provider.
 * `provider` = "razorpay" | "cashfree"
 */
export async function startCheckout({
  provider,
  appOrderId,
  cashfreeSessionId,
  buyer,
  onSuccess,
  onFailure,
}) {
  if (provider === "razorpay") {
    return _payWithRazorpay({ appOrderId, buyer, onSuccess, onFailure });
  }
  return _payWithCashfree({ cashfreeSessionId, onSuccess, onFailure });
}

/** Invalidate the providers cache — call after admin toggles a provider. */
export function invalidatePaymentProviders() { _providersCache = null; }
